import type { GameId } from '@chessmate/protocol';
import { getRemoteStages, setRemoteStages } from '../lib/remote-stage-store.js';
import { webOnlyStages } from '../lib/web-only-games.js';
import { ADULT } from './adult.js';
import { KIDS } from './kids.js';
import { effectiveOverrides, parseIncluded, parseRemoteStages, parseStages, visibleGames } from './rollout.js';
import type { EditionConfig, GameStage } from './types.js';

/** Replaced by Vite's `define` with the literal edition at build time; undefined in a bare test run. */
declare const __EDITION__: string | undefined;
/** The games in this build (`S4K_GAMES`, validated by the Vite config); undefined or blank means the whole allow-list. */
declare const __GAMES__: string | undefined;
/** The web-only games enabled in this (web) build (`S4K_WEB_ONLY_GAMES`, validated by the Vite config); never set in a native build. */
declare const __WEB_ONLY__: string | undefined;
/** The adult-only games in this build (`src/edition/adult-only.ts`, set by the Vite config): empty in every child build. */
declare const __ADULT_ONLY__: string | undefined;

// The comparison folds to a constant at build time, so the edition that is NOT chosen is never referenced and is
// removed from the bundle (the guard test scans the built output to prove it).
export const edition: EditionConfig = typeof __EDITION__ !== 'undefined' && __EDITION__ === 'adult' ? ADULT : KIDS;

// Presence (build time) and readiness (stages) on top of the edition's allow-list. The build already rejected anything
// invalid, so a bad token here is ignored rather than fatal.
// The build validated this list against the edition's web-only games (`web-only.ts`, tooling only), and it is empty unless the switch is on.
const webOnlyEnabled = ((typeof __WEB_ONLY__ !== 'undefined' ? __WEB_ONLY__ : '') as string).split(',').filter((g): g is GameId => g !== '');
/** The edition's games plus the web-only ones this web build enabled: the allow-list every later step narrows. */
const adultOnlyEnabled = ((typeof __ADULT_ONLY__ !== 'undefined' ? __ADULT_ONLY__ : '') as string).split(',').filter((g): g is GameId => g !== '');
const allowList: readonly GameId[] = [...edition.games, ...webOnlyEnabled, ...adultOnlyEnabled];
const included = parseIncluded(typeof __GAMES__ !== 'undefined' ? __GAMES__ : '', allowList).value;
const stageOverrides = parseStages(import.meta.env.VITE_GAME_STAGES, allowList).value;

export const BETA_STORAGE_KEY = 's4k:beta';

/**
 * The words on screen for a robot level: the edition's own name for it when it has one (the adult edition's calm ladder), else the shared
 * protocol name (Kitten, Puppy, ...). Only the label differs: the level id, its difficulty and every test id stay the same.
 */
export function levelLabel(level: { readonly id: string; readonly name: string }): string {
  return edition.levelNames?.[level.id] ?? level.name;
}

/** The noun after the level in the game panel's title ("Kitten battle" in the child edition). */
export const gameNoun: string = edition.gameNoun ?? 'battle';

/**
 * Has this person opted in to beta games? Remembered on the device. `?beta=1` opts in and `?beta=0` opts out (handy for
 * QA and a parent's link); storage that is blocked just means the choice lasts for this visit.
 */
export function betaOptIn(search: string = typeof window === 'undefined' ? '' : window.location.search): boolean {
  const param = new URLSearchParams(search).get('beta');
  try {
    if (param === '1') localStorage.setItem(BETA_STORAGE_KEY, '1');
    else if (param === '0') localStorage.removeItem(BETA_STORAGE_KEY);
    else return localStorage.getItem(BETA_STORAGE_KEY) === '1';
    return param === '1';
  } catch {
    return param === '1';
  }
}

/** The games to offer right now: the edition's allow-list, narrowed by presence, then by stage and the beta opt-in. */
export function enabledGames(beta: boolean = betaOptIn()): GameId[] {
  // A web-only game brings its own default stage (it registers itself when its module is in the build; see lib/web-only-games.ts).
  // Stage precedence: the operator's remote value, then VITE_GAME_STAGES, then the edition default (docs/proposals/remote-game-stages.md).
  return visibleGames({
    allowList,
    included,
    stages: { ...edition.stages, ...webOnlyStages() },
    overrides: effectiveOverrides(stageOverrides, getRemoteStages()),
    beta,
  });
}

/**
 * Take the operator's remote stages (the body of `GET /api/game-stages`, or a cached copy). Validated against THIS build's games, so it can
 * change the stage of a game that is already here and can never add one (a remote `prod` for a web-only game in a default or native build, or
 * for an adult-only game in a child build, is dropped because that game is not in `allowList`). Returns the stages that were accepted, or
 * null when the payload was not the expected shape (then nothing changes).
 */
export function acceptRemoteStages(payload: unknown): Partial<Record<GameId, GameStage>> | null {
  const accepted = parseRemoteStages(payload, allowList);
  if (accepted === null) return null;
  setRemoteStages(accepted);
  return accepted;
}

export type { Edition, EditionBrand, EditionConfig, GameStage } from './types.js';
